import React, { useState } from 'react';
import { runPayroll } from '../../api/admin';
import PayrollDetailModal from './PayrollDetailModal';
import './Payroll.css';

const DEFAULT_HOURLY_RATE = 21;

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

function Payroll() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState(null);
  const [detailedResults, setDetailedResults] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRunPayroll = async () => {
    if (!startDate || !endDate) {
      setError('Please select both a start date and an end date.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const response = await runPayroll(startDate, endDate);
      if (response?.error) {
        setError('Failed to run payroll. Please try again.');
        setResults(null);
      } else {
        const totalResults = response?.data?.totalResults || response?.totalResults || [];
        const detailed = response?.data?.detailedResults || response?.detailedResults || [];
        setDetailedResults(detailed);

        const merged = totalResults.map(coach => {
          const totalHours =
            (coach.setupHours || 0) +
            (coach.programHours || 0) +
            (coach.equipmentAndPrepHours || 0);
          const hourlyRate = coach.hourlyRate || DEFAULT_HOURLY_RATE;
          const hourlyPay = totalHours * hourlyRate;
          return {
            name: coach.name,
            setupHours: coach.setupHours || 0,
            programHours: coach.programHours || 0,
            prepHours: coach.equipmentAndPrepHours || 0,
            totalHours,
            hourlyRate,
            hourlyPay,
            total: hourlyPay
          };
        });

        setResults(merged);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payroll-page">
      <h1>Payroll</h1>

      <div className="payroll-controls">
        <div className="payroll-date-group">
          <label htmlFor="payroll-start-date">Start Date</label>
          <input
            id="payroll-start-date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="payroll-date-input"
          />
        </div>
        <div className="payroll-date-group">
          <label htmlFor="payroll-end-date">End Date</label>
          <input
            id="payroll-end-date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="payroll-date-input"
          />
        </div>
        <button
          className="payroll-run-button"
          onClick={handleRunPayroll}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Run Payroll'}
        </button>
      </div>

      {error && <div className="payroll-error">{error}</div>}

      {results && results.length === 0 && (
        <div className="payroll-empty">No payroll data found for the selected date range.</div>
      )}

      {results && results.length > 0 && (
        <div className="payroll-table-wrapper">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Setup Hrs</th>
                <th>Program Hrs</th>
                <th>Prep Hrs</th>
                <th>Total Hrs</th>
                <th>Rate</th>
                <th>Hourly Pay</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {results.map((coach, idx) => (
                <tr key={idx} className="payroll-row-clickable" onClick={() => setSelectedCoach(coach.name)}>
                  <td className="payroll-coach-name">{coach.name}</td>
                  <td>{coach.setupHours}</td>
                  <td>{coach.programHours}</td>
                  <td>{coach.prepHours}</td>
                  <td>{coach.totalHours}</td>
                  <td>{formatCurrency(coach.hourlyRate)}</td>
                  <td>{formatCurrency(coach.hourlyPay)}</td>
                  <td className="payroll-total-cell">{formatCurrency(coach.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCoach && (
        <PayrollDetailModal
          coachName={selectedCoach}
          detailedResults={detailedResults}
          onClose={() => setSelectedCoach(null)}
        />
      )}
    </div>
  );
}

export default Payroll;
